import { useState, useEffect, useMemo } from 'react'
import Select from 'react-select'
import { selectCustomStyles } from '../../../../utils/Select'
import Toast, { ToastType } from '../../../../utils/toast'
import {
  useAddInvestorToUserMutation,
  useGetAllFundsOfUserQuery,
} from '../../../../generated/graphql'
import { graphqlRequestClient, queryClient } from '../../../../queries/client'
import { getUserId } from '../../../../utils/SessionStorageUtils'
import { InvestorType } from '../../../../app/pages/accounts/components/AddInvestor'
import { KTSVG } from '../../../../_metronic/helpers'

interface UserOption {
  label: string
  value: string
  name: string
  email: string
  accountId?: string
  role?: string | null
  isAccountAdmin?: boolean
}

const getIsAccountAdmin = (): boolean => {
  const stored = sessionStorage.getItem('isAccountAdmin')
  return stored === 'true'
}

interface FundOption {
  label: string
  value: number
  name: string
  type?: string
}

interface AddFundPopupProps {
  show: boolean
  onHide: () => void
  selectedUserId: string
  selectedUserName: string
  selectedUserAccountId: string
  selectedUserRole: string | null
  selectedUserIsAccountAdmin: boolean
  allUsers: UserOption[]
}

const AddFundPopup: React.FC<AddFundPopupProps> = ({
  show,
  onHide,
  selectedUserId,
  selectedUserName,
  selectedUserIsAccountAdmin,
  allUsers,
}) => {
  const [selectedFund, setSelectedFund] = useState<FundOption[]>([])
  const [newFundName, setNewFundName] = useState('')
  const [newFundType, setNewFundType] = useState('')
  const [addToOthers, setAddToOthers] = useState(false)
  const [selectedUsers, setSelectedUsers] = useState<string[]>([])
  const [loading, setLoading] = useState(false)

  const currentUserId = getUserId()
  const isAccountAdmin = getIsAccountAdmin()

  const isAdmin =
    String(selectedUserId) === String(currentUserId) ? isAccountAdmin : selectedUserIsAccountAdmin

  const { data: adminFundsData, isLoading: adminLoading } = useGetAllFundsOfUserQuery(
    graphqlRequestClient,
    { input: currentUserId },
    { enabled: show && !!currentUserId }
  )

  const { data: userFundsData, isLoading: userLoading } = useGetAllFundsOfUserQuery(
    graphqlRequestClient,
    { input: selectedUserId },
    { enabled: show && !!selectedUserId }
  )

  const mutateAddInvestor = useAddInvestorToUserMutation<Error>(graphqlRequestClient, {}, {})

  const adminFunds = adminFundsData?.getAllFundsOfUser || []
  const userFunds = userFundsData?.getAllFundsOfUser || []

  const userFundNames = useMemo(() => {
    return new Set(userFunds.map((f: any) => f.name?.toLowerCase()))
  }, [userFunds])

  const availableFundsForNonAdmin = useMemo(() => {
    return adminFunds.filter((fund: any) => !userFundNames.has(fund.name?.toLowerCase()))
  }, [adminFunds, userFundNames])

  // Memoized fund options for non-admin flow
  const fundOptionsForNonAdmin = useMemo(() => {
    return availableFundsForNonAdmin.map((fund: any) => ({
      label: fund.name,
      value: fund.id,
      name: fund.name,
      type: fund.type,
    }))
  }, [availableFundsForNonAdmin])

  // Memoized other user options
  const otherUserOptions = useMemo(() => {
    return allUsers
      .filter((u) => u.value !== selectedUserId)
      .map((u) => ({
        label: u.name,
        value: u.value,
        email: u.email,
      }))
  }, [allUsers, selectedUserId])

  // Memoized selected users value for react-select (prevents re-creation on every render)
  const selectedUsersValue = useMemo(() => {
    return otherUserOptions.filter((o) => selectedUsers.includes(o.value))
  }, [otherUserOptions, selectedUsers])

  // Memoized fund type options
  const fundTypeOptions = useMemo(() => {
    return Object.entries(InvestorType).map(([_, value]) => ({
      label: value,
      value: value,
    }))
  }, [])

  useEffect(() => {
    if (!show) {
      setSelectedFund([])
      setNewFundName('')
      setNewFundType('')
      setAddToOthers(false)
      setSelectedUsers([])
    }
  }, [show])

  const addFundToUser = async (targetUserId: string, fundName: string, fundType: string) => {
    return mutateAddInvestor.mutateAsync({
      input: {
        userId: targetUserId,
        fundName: fundName.trim(),
        fundType,
      },
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (isAdmin) {
      if (!newFundName.trim()) {
        Toast('Please enter a fund name', ToastType.error)
        return
      }
      if (!newFundType.trim()) {
        Toast('Please select a fund type', ToastType.error)
        return
      }
    } else {
      if (selectedFund.length === 0) {
        Toast('Please select at least one fund', ToastType.error)
        return
      }
    }

    setLoading(true)
    try {
      const usersToReceive: string[] = [selectedUserId]

      if (isAdmin && addToOthers && selectedUsers.length) {
        usersToReceive.push(...selectedUsers)
      }

      if (isAdmin) {
        const fundName = newFundName.trim()
        const fundType = newFundType.trim()

        for (const uid of usersToReceive) {
          await addFundToUser(uid, fundName, fundType)
        }

        const total = usersToReceive.length
        const others = total - 1
        Toast(
          others > 0
            ? `Fund "${fundName}" added to ${selectedUserName} and ${others} other user(s)`
            : `Fund "${fundName}" added to ${selectedUserName}`,
          ToastType.success
        )
      } else {
        for (const fund of selectedFund) {
          for (const uid of usersToReceive) {
            await addFundToUser(uid, fund.name, fund.type || '')
          }
        }

        const fundCount = selectedFund.length
        Toast(
          fundCount === 1
            ? `Fund "${selectedFund[0].name}" added to ${selectedUserName}`
            : `${fundCount} funds added to ${selectedUserName}`,
          ToastType.success
        )
      }

      queryClient.invalidateQueries()
      onHide()
    } catch (err: any) {
      const msg = err?.response?.errors?.[0]?.message || err?.message || 'Failed to add fund'
      Toast(msg, ToastType.error)
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (!loading) {
      setSelectedFund([])
      setNewFundName('')
      setNewFundType('')
      setAddToOthers(false)
      setSelectedUsers([])
      onHide()
    }
  }

  if (!show) return null

  const loadingFunds = adminLoading || userLoading

  return (
    <div
      className='modal fade show d-block'
      tabIndex={-1}
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
    >
      <div className='modal-dialog modal-dialog-centered' style={{ maxWidth: '600px' }}>
        <div className='modal-content'>
          <div className='modal-header'>
            <div className='col d-flex justify-content-between px-1'>
              <div className='py-3'>
                <h3 className='fw-bold text-dark'>Add Fund to {selectedUserName}</h3>
              </div>
              <button
                className='btn btn-sm btn-icon btn-active-light-primary mt-0'
                type='button'
                data-bs-toggle='tooltip'
                title='Close'
                onClick={handleClose}
                disabled={loading}
              >
                <KTSVG className='svg-icon-1' path='/media/icons/duotune/arrows/arr061.svg' />
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className='modal-body'>
              {/* Admin: Create New Fund */}
              {isAdmin && (
                <>
                  <div className='mb-4'>
                    <label className='form-label fw-semibold required'>Fund Name</label>
                    <input
                      type='text'
                      className='form-control'
                      placeholder='Enter Fund Name'
                      value={newFundName}
                      onChange={(e) => setNewFundName(e.target.value)}
                      disabled={loading}
                    />
                  </div>

                  <div className='mb-5'>
                    <label className='form-label fw-semibold required'>Fund Type</label>
                    <Select
                      options={fundTypeOptions}
                      styles={selectCustomStyles as any}
                      placeholder='Select fund type'
                      value={fundTypeOptions.find((o) => o.value === newFundType) || null}
                      onChange={(opt: any) => setNewFundType(opt?.value ?? '')}
                      isDisabled={loading}
                      isSearchable
                    />
                  </div>
                </>
              )}

              {/* Non-Admin: Select Existing Funds */}
              {!isAdmin && (
                <div className='mb-5'>
                  <label className='form-label fw-semibold required'>Select Funds</label>

                  {loadingFunds ? (
                    <div className='d-flex align-items-center justify-content-center py-4'>
                      <span className='spinner-border spinner-border-sm me-2' />
                      <span className='text-muted'>Loading funds...</span>
                    </div>
                  ) : fundOptionsForNonAdmin.length === 0 ? (
                    <div className='alert alert-warning d-flex align-items-center'>
                      <i className='bi bi-exclamation-triangle-fill fs-3 me-3' />
                      <div className='fs-7'>
                        No available funds. The user already has all your funds.
                      </div>
                    </div>
                  ) : (
                    <Select
                      options={fundOptionsForNonAdmin}
                      styles={selectCustomStyles as any}
                      placeholder='Select funds'
                      value={selectedFund}
                      onChange={(opts: any) => setSelectedFund(opts || [])}
                      isDisabled={loading}
                      isMulti
                      closeMenuOnSelect={false}
                      formatOptionLabel={(opt: FundOption) => (
                        <div className='d-flex flex-column'>
                          <span className='fw-semibold'>{opt.name}</span>
                          {opt.type && <span className='text-muted fs-8'>{opt.type}</span>}
                        </div>
                      )}
                    />
                  )}
                </div>
              )}

              {/* Add to Other Users (Only if target user is Account Admin) */}
              {selectedUserIsAccountAdmin && (
                <div className='mb-5'>
                  <div className='d-flex align-items-center justify-content-between p-4 bg-light-subtle rounded'>
                    <div>
                      <label className='form-label fw-semibold mb-1'>Add to Other Users</label>
                      <div className='text-muted fs-7'>
                        Enable to add this fund to other users in the account
                      </div>
                    </div>
                    <div className='form-check form-switch form-check-custom form-check-solid'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        checked={addToOthers}
                        onChange={(e) => {
                          setAddToOthers(e.target.checked)
                          if (!e.target.checked) setSelectedUsers([])
                        }}
                        disabled={loading}
                      />
                    </div>
                  </div>

                  {addToOthers && (
                    <div className='mt-4'>
                      <label className='form-label fw-semibold'>Select Users</label>
                      <Select
                        isMulti
                        options={otherUserOptions}
                        styles={selectCustomStyles as any}
                        placeholder='Select users...'
                        value={selectedUsersValue}
                        onChange={(selected: any) =>
                          setSelectedUsers(selected ? selected.map((s: any) => s.value) : [])
                        }
                        formatOptionLabel={(opt: any) => (
                          <div className='d-flex flex-column'>
                            <span className='fw-semibold'>{opt.label}</span>
                            <span className='text-muted fs-8'>{opt.email}</span>
                          </div>
                        )}
                        isDisabled={loading}
                      />
                      {selectedUsers.length > 0 && (
                        <div className='text-muted mt-2 fs-7'>
                          {selectedUsers.length} user(s) selected
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className='d-flex justify-content-end gap-3 mt-4'>
                <button
                  type='button'
                  className='btn btn-light'
                  onClick={handleClose}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  className='btn btn-primary'
                  disabled={
                    loading ||
                    (isAdmin
                      ? !newFundName.trim() || !newFundType.trim()
                      : selectedFund.length === 0 || loadingFunds)
                  }
                >
                  {loading ? (
                    <>
                      <span className='spinner-border spinner-border-sm me-2' />
                      Adding...
                    </>
                  ) : (
                    'Add Fund'
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

// Optional: Prevent unnecessary re-renders from parent
export default AddFundPopup